"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import * as m from "motion/react-m";
import { AnimatePresence } from "motion/react";
import {
  IconImage,
  IconVideo,
  IconQuality,
  IconAspectRatio,
  PromptContentArea,
  PromptTextInput,
  PromptControlBar,
  ContentPlaceholder,
  ContentImage,
  ContentProcessingOverlay,
  ControlItem,
  SendButton,
} from "@ui/components/demos/ImageVideoHero/components";
import { pxToRem } from "@ui/lib/utils";
import { VideoPlayer } from "@ui/components/demos/ImageVideoHero/VideoPlayer";
import { TRANSITIONS } from "@ui/components/demos/ImageVideoHero/types";

export interface ExternalModelDemoProps {
  step: "select-model" | "enter-prompt" | "generate";
  modelName?: string | null;
  prompt?: string | null;
  modality?: "image" | "video" | null;
  models?: string[] | null;
  assetUrl?: string | null;
  contentUrl?: string | null;
}

export default function ExternalModelDemo({
  step,
  modelName,
  prompt,
  modality,
  models,
  assetUrl,
  contentUrl,
}: ExternalModelDemoProps) {
  const showAsset = step === "enter-prompt" || step === "generate";
  const showPromptText = step === "enter-prompt" || step === "generate";
  const showModelSelector = step === "select-model";

  const [generationComplete, setGenerationComplete] = useState(false);

  useEffect(() => {
    if (step !== "generate") {
      setGenerationComplete(false);
      return;
    }
    const timer = setTimeout(() => setGenerationComplete(true), 1000);
    return () => clearTimeout(timer);
  }, [step]);

  const showProcessing = step === "generate" && !generationComplete;

  return (
    <div className='tw-h-full tw-w-full tw-relative'>
      {/* Grid background — purely decorative, extends beyond right edge */}
      <div
        className='tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-left-3 tw-flex tw-flex-col tw-gap-3'
        style={{ width: "130%" }}
      >
        <div className='tw-grid tw-grid-cols-2 tw-gap-3'>
          <div
            className='tw-aspect-[4/3] tw-rounded-2xl tw-relative tw-overflow-hidden'
            style={{ background: "#EBE9E6CC" }}
          >
            <AnimatePresence>
              {generationComplete && (contentUrl || assetUrl) && (
                <m.div
                  key='result'
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={TRANSITIONS.slow}
                  className='tw-absolute tw-inset-0'
                >
                  {modality === "video" && contentUrl ? (
                    <VideoPlayer src={contentUrl} autoPlay loop muted rounded={false} />
                  ) : (
                    <Image src={contentUrl || assetUrl!} alt='' fill className='tw-object-cover' />
                  )}
                </m.div>
              )}
            </AnimatePresence>
          </div>
          <div className='tw-aspect-[4/3] tw-rounded-2xl' style={{ background: "#EBE9E6CC" }} />
        </div>
        <div className='tw-grid tw-grid-cols-2 tw-gap-3'>
          <div
            className='tw-aspect-[4/3] tw-rounded-2xl'
            style={{ background: "linear-gradient(to bottom, #EBE9E6CC, #F5F3F1)" }}
          />
          <div
            className='tw-aspect-[4/3] tw-rounded-2xl'
            style={{ background: "linear-gradient(to bottom, #EBE9E6CC, #F5F3F1)" }}
          />
        </div>
      </div>

      {/* Omnibox card — centered in the full component area, unaffected by the grid */}
      <m.div
        className='tw-h-full tw-w-full tw-flex tw-items-center tw-justify-center tw-relative tw-z-10'
        animate={{ opacity: generationComplete ? 0 : 1 }}
        transition={TRANSITIONS.slow}
        style={{ pointerEvents: generationComplete ? "none" : "auto" }}
      >
        <div
          className='tw-w-full tw-max-w-xl tw-p-3 tw-bg-white'
          style={{
            boxShadow:
              "0 0 1px 0 rgba(0, 0, 0, 0.40), 0 1px 2px 0 rgba(0, 0, 0, 0.04), 0 2px 2px 0 rgba(0, 0, 0, 0.04)",
            borderRadius: "30px",
          }}
        >
          {/* Content area */}
          <PromptContentArea sequencedMotion={m}>
            {!showAsset && <ContentPlaceholder sequencedMotion={m} />}
            {showAsset && assetUrl && <ContentImage sequencedMotion={m} src={assetUrl} alt='' />}
            {showProcessing && (
              <ContentProcessingOverlay
                sequencedMotion={m}
                className='tw-rounded-[14px]'
                spinner={
                  <div className='tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-animate-spin' />
                }
              />
            )}
          </PromptContentArea>

          {/* Prompt text area */}
          <PromptTextInput sequencedMotion={m}>
            {showPromptText && prompt ? (
              <span className='tw-line-clamp-1 tw-type-xs tw-py-1 tw-text-neutral-500'>{prompt}</span>
            ) : (
              <span className='tw-opacity-50 tw-type-xs tw-py-1 tw-text-neutral-500'>Write a prompt...</span>
            )}
          </PromptTextInput>

          {/* Control bar */}
          <PromptControlBar
            sequencedMotion={m}
            controls={
              <>
                <div className='tw-relative tw-flex tw-items-center tw-gap-1 tw-text-neutral-500'>
                  {modality === "image" ? <IconImage className='tw-size-5' /> : <IconVideo className='tw-size-5' />}
                  <span className='tw-type-xs tw-py-1'>{modelName || "Model"}</span>

                  {/* Model selector dropdown */}
                  {showModelSelector && models && models.length > 0 && (
                    <div className='tw-absolute tw-left-0 tw-top-full tw-mt-2 tw-rounded-2xl tw-overflow-hidden tw-shadow-lg tw-z-20 tw-border tw-border-black/10 tw-bg-white'>
                      {[...models]
                        .sort((a, b) => {
                          if (a === modelName) return -1;
                          if (b === modelName) return 1;
                          return 0;
                        })
                        .map(model => {
                          const isSelected = model === modelName;
                          return (
                            <div
                              key={model}
                              className='tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-relative'
                              style={{ minWidth: pxToRem(180) }}
                            >
                              {isSelected && <div className='tw-absolute tw-inset-1 tw-rounded-xl tw-bg-cream' />}
                              <span className='tw-relative tw-z-10 tw-type-xs tw-py-1'>{model}</span>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
                <ControlItem
                  sequencedMotion={m}
                  icon={<IconQuality className='tw-size-5' />}
                  label='720p'
                  className='tw-hidden md:tw-flex'
                />
                <ControlItem
                  sequencedMotion={m}
                  icon={<IconAspectRatio className='tw-size-5' />}
                  label='16:9'
                  className='tw-hidden md:tw-flex'
                />
              </>
            }
            button={<SendButton sequencedMotion={m} />}
          />
        </div>
      </m.div>
    </div>
  );
}
